import * as React from 'react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import styled from 'styled-components';
import {
  ChevronDown,
  LogOut,
  Monitor,
  Moon,
  Sparkles,
  Sun,
  UserRound,
  X,
} from 'lucide-react';

import { LAYERS } from 'components/constants/layers';
import { useLanguage } from 'core/hooks';
import { AppLanguage } from 'core/state/languageState';
import { setThemeMode, themeModeVar, ThemeMode } from 'core/state/themeState';
import { T } from 'services/ThemeTokens';
import { MobileIconButton } from 'molecules/MobileIconButton';

import type { IMenuGroup } from './MenuContainer';
import { findActiveLeaf, isPathActive } from './mobileNav';

export interface IMobileMenuSheet {
  id: string;
  open: boolean;
  onClose: () => void;
  /** Группы из `getVisibleMenu` — те же, что у компьютерной шапки. */
  groups: IMenuGroup[];
  pathname: string;
  /** Счётчики пункта (`menuLeafCounters` / очередь базы знаний). */
  badgeOf: (to: string) => ReactNode;
  /** Есть ли счётчик хоть у одного пункта группы — точка у свёрнутой. */
  groupHasNew: (group: IMenuGroup) => boolean;
  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
  profileTo?: string | null;
  aiChatTo?: string | null;
  onExit: () => void;
}

const THEME_MODES: { mode: ThemeMode; Icon: typeof Sun }[] = [
  { mode: 'system', Icon: Monitor },
  { mode: 'light', Icon: Sun },
  { mode: 'dark', Icon: Moon },
];

/** Имена языков — самоназвания: их не переводят, чтец читает их на своём
 * языке (`lang`). */
const LANGUAGES: { lang: AppLanguage; name: string }[] = [
  { lang: 'ru', name: 'Русский' },
  { lang: 'pl', name: 'Polski' },
];

/**
 * Стрелки внутри группы радиокнопок (WAI-ARIA radiogroup): ←/↑ и →/↓
 * выбирают соседнюю по кругу, Home/End — первую и последнюю; фокус
 * переходит на выбранную. Tab попадает только на выбранную (`tabIndex`).
 */
const onRadioKeys = <V,>(
  event: React.KeyboardEvent<HTMLDivElement>,
  values: V[],
  current: V,
  select: (value: V) => void,
) => {
  const index = values.indexOf(current);
  let next = -1;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    next = (index + 1) % values.length;
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    next = (index - 1 + values.length) % values.length;
  } else if (event.key === 'Home') {
    next = 0;
  } else if (event.key === 'End') {
    next = values.length - 1;
  }
  if (next < 0) return;
  event.preventDefault();
  select(values[next]);
  const radios = event.currentTarget.querySelectorAll<HTMLElement>(
    '[role="radio"]',
  );
  radios[next]?.focus();
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * ТЗ 0121 — меню ERP на телефоне: полноэкранный лист с группами-аккордеонами.
 *
 * Данных своих нет: группы — `getVisibleMenu`, счётчики — те же функции, что
 * у шапки (`menuLeafCounters`, `menuKbPending`). Внизу — профиль, ИИ-чат,
 * тема, язык и выход (на телефоне их нет в шапке).
 *
 * Закрывается по крестику, Esc и переходу; пока открыт — фокус заперт внутри
 * (`aria-modal`), страница под ним не прокручивается.
 */
/**
 * Сделать узел недоступным (`inert` + `aria-hidden`) и вернуть функцию,
 * которая восстанавливает прежние значения.
 */
const makeInert = (node: HTMLElement | null): (() => void) => {
  if (!node) return () => undefined;
  const hadInert = node.hasAttribute('inert');
  const ariaHidden = node.getAttribute('aria-hidden');
  node.setAttribute('inert', '');
  node.setAttribute('aria-hidden', 'true');
  return () => {
    if (!hadInert) node.removeAttribute('inert');
    if (ariaHidden === null) node.removeAttribute('aria-hidden');
    else node.setAttribute('aria-hidden', ariaHidden);
  };
};

export const MobileMenuSheet = ({
  id,
  open,
  onClose,
  groups,
  pathname,
  badgeOf,
  groupHasNew,
  userFirstName,
  userLastName,
  userEmail,
  profileTo,
  aiChatTo,
  onExit,
}: IMobileMenuSheet) => {
  const { t } = useTranslation();
  const themeMode = useReactiveVar(themeModeVar);
  const { language, setLanguage } = useLanguage();
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Раскрыта группа текущей страницы; остальные — по нажатию.
  const activeGroup =
    groups.find((group) =>
      group.children.some((leaf) =>
        isPathActive(pathname, leaf.match ?? leaf.to),
      ),
    )?.text ?? null;
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(activeGroup ? [activeGroup] : []),
  );

  useEffect(() => {
    if (open && activeGroup) {
      setExpanded((prev) =>
        prev.has(activeGroup)
          ? prev
          : new Set(Array.from(prev).concat(activeGroup)),
      );
    }
  }, [open, activeGroup]);

  // Esc, ловушка фокуса, запрет прокрутки страницы под листом; страница
  // под листом (#root — лист порталом вне него) недоступна чтецу и Tab.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    const openedAt = window.location.pathname;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const restoreInert = makeInert(document.getElementById('root'));
    closeRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !sheetRef.current) return;
      const nodes = Array.from(
        sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      restoreInert();
      // Закрыли переходом — фокус на заголовок новой страницы, а не на «Ещё»
      // (кнопка той же панели, а человек уже на другой странице).
      if (window.location.pathname !== openedAt) {
        const heading = document.querySelector<HTMLElement>(
          '[data-page-heading]',
        );
        if (heading) {
          heading.focus({ preventScroll: true });
          return;
        }
      }
      previous?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const toggleGroup = (text: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(text)) next.delete(text);
      else next.add(text);
      return next;
    });

  const fullName = [userFirstName, userLastName].filter(Boolean).join(' ');

  // Подсвечен один пункт — самый точный (ТЗ 0122: на `/mail/contacts` —
  // «Контакты», а не ещё и «Почта» `/mail`).
  const activeLeafTo = findActiveLeaf(pathname, groups)?.to ?? null;

  const leafRow = (
    leaf: IMenuGroup['children'][number],
    nested: boolean,
  ): ReactNode => {
    const active = leaf.to === activeLeafTo;
    const LeafIcon = leaf.icon;
    return (
      <li key={leaf.to}>
        <Row
          to={leaf.to}
          $nested={nested}
          $active={active}
          aria-current={active ? 'page' : undefined}
          onClick={onClose}
        >
          <LeafIcon size={18} strokeWidth={2} aria-hidden="true" />
          <RowText>{leaf.text}</RowText>
          {badgeOf(leaf.to)}
        </Row>
      </li>
    );
  };

  return createPortal(
    <Sheet
      ref={sheetRef}
      id={id}
      role="dialog"
      aria-modal="true"
      aria-label={t('mobile.menu.title')}
      data-testid="mobile-menu-sheet"
    >
      <Head>
        <HeadTitle>{t('mobile.menu.title')}</HeadTitle>
        <MobileIconButton
          ref={closeRef}
          icon={X}
          onClick={onClose}
          aria-label={t('mobile.menu.close')}
        />
      </Head>

      <Body>
        <nav aria-label={t('mobile.menu.navLabel')}>
          <List>
            {groups.map((group, index) => {
              if (group.children.length === 1) {
                return leafRow(group.children[0], false);
              }
              const isOpen = expanded.has(group.text);
              // Номер группы, а не её текст: в id нельзя пробелы и
              // кириллицу, а текст ещё и меняется с языком.
              const panelId = `${id}-group-${index}`;
              return (
                <li key={group.text}>
                  <GroupHead
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggleGroup(group.text)}
                  >
                    <RowText>{group.text}</RowText>
                    {!isOpen && groupHasNew(group) ? (
                      <NewDot
                        role="img"
                        aria-label={t('menu.counters.groupHasNew')}
                      />
                    ) : null}
                    <Chevron $open={isOpen}>
                      <ChevronDown size={18} aria-hidden="true" />
                    </Chevron>
                  </GroupHead>
                  {/* Панель есть всегда (для aria-controls), свёрнутая —
                      скрыта атрибутом hidden. */}
                  <List id={panelId} hidden={!isOpen}>
                    {group.children.map((leaf) => leafRow(leaf, true))}
                  </List>
                </li>
              );
            })}
          </List>
        </nav>

        <Section>
          {profileTo && (fullName || userEmail) ? (
            <ProfileRow to={profileTo} onClick={onClose}>
              <UserRound size={18} strokeWidth={2} aria-hidden="true" />
              <ProfileText>
                <strong>{fullName || userEmail}</strong>
                {fullName && userEmail ? <span>{userEmail}</span> : null}
              </ProfileText>
            </ProfileRow>
          ) : null}
          {aiChatTo ? (
            <ProfileRow to={aiChatTo} onClick={onClose}>
              <Sparkles size={18} strokeWidth={2} aria-hidden="true" />
              <RowText>{t('aiChat.headerButton')}</RowText>
            </ProfileRow>
          ) : null}

          <SettingLabel id={`${id}-theme`}>{t('theme.label')}</SettingLabel>
          <Segmented
            role="radiogroup"
            aria-labelledby={`${id}-theme`}
            onKeyDown={(event) =>
              onRadioKeys(
                event,
                THEME_MODES.map(({ mode }) => mode),
                themeMode,
                setThemeMode,
              )
            }
          >
            {THEME_MODES.map(({ mode, Icon }) => (
              <Segment
                key={mode}
                type="button"
                role="radio"
                aria-checked={themeMode === mode}
                tabIndex={themeMode === mode ? 0 : -1}
                $active={themeMode === mode}
                onClick={() => setThemeMode(mode)}
              >
                <Icon size={16} strokeWidth={2} aria-hidden="true" />
                {t(`theme.${mode}`)}
              </Segment>
            ))}
          </Segmented>

          <SettingLabel id={`${id}-lang`}>
            {t('mobile.menu.language')}
          </SettingLabel>
          <Segmented
            role="radiogroup"
            aria-labelledby={`${id}-lang`}
            onKeyDown={(event) =>
              onRadioKeys(
                event,
                LANGUAGES.map(({ lang }) => lang),
                language,
                setLanguage,
              )
            }
          >
            {LANGUAGES.map(({ lang, name }) => (
              <Segment
                key={lang}
                type="button"
                role="radio"
                lang={lang}
                aria-checked={language === lang}
                tabIndex={language === lang ? 0 : -1}
                $active={language === lang}
                onClick={() => setLanguage(lang)}
              >
                {name}
              </Segment>
            ))}
          </Segmented>

          <ExitRow type="button" onClick={onExit}>
            <LogOut size={18} strokeWidth={2} aria-hidden="true" />
            <RowText>{t('button.exit')}</RowText>
          </ExitRow>
        </Section>
      </Body>
    </Sheet>,
    document.body,
  );
};

const Sheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${LAYERS.MOBILE_SHEET};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100vh;
  height: 100dvh;
  padding: env(safe-area-inset-top) env(safe-area-inset-right) 0
    env(safe-area-inset-left);
  background: ${T.surface};
  color: ${T.text};
`;

const Head = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 4px 0 16px;
  border-bottom: 1px solid ${T.border};
`;

const HeadTitle = styled.h2`
  flex: 1;
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${T.text};
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px 0 calc(16px + env(safe-area-inset-bottom));
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const rowBase = `
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 0 16px;
  border: none;
  background: none;
  font: inherit;
  font-size: 15px;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  & > svg {
    flex: none;
  }
`;

const Row = styled(Link)<{ $nested: boolean; $active: boolean }>`
  ${rowBase}
  padding-left: ${({ $nested }) => ($nested ? '28px' : '16px')};
  color: ${({ $active }) => ($active ? T.accentText : T.text)};
  background: ${({ $active }) => ($active ? T.selected : 'none')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};

  & > svg {
    color: ${({ $active }) => ($active ? T.accentText : T.textMuted)};
  }

  &:hover,
  &:focus {
    color: ${({ $active }) => ($active ? T.accentText : T.text)};
  }

  &:active {
    background: ${T.hover};
  }
`;

const RowText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GroupHead = styled.button`
  ${rowBase}
  color: ${T.text};
  font-weight: 600;

  &:active {
    background: ${T.hover};
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: ${T.textMuted};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0')});
  transition: transform 0.2s ease;
`;

const NewDot = styled.span`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${T.accent};
`;

const Section = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${T.border};
`;

const ProfileRow = styled(Link)`
  ${rowBase}
  color: ${T.text};

  & > svg {
    color: ${T.textMuted};
  }

  &:hover,
  &:focus {
    color: ${T.text};
  }
`;

const ProfileText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;

  & > strong,
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > span {
    font-size: 13px;
    color: ${T.textMuted};
  }
`;

const SettingLabel = styled.div`
  padding: 12px 16px 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${T.textMuted};
`;

const Segmented = styled.div`
  display: flex;
  gap: 4px;
  margin: 0 16px;
  padding: 3px;
  border-radius: 10px;
  background: ${T.fill};
`;

const Segment = styled.button<{ $active: boolean }>`
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  height: 40px;
  padding: 0 6px;
  border: 1px solid ${({ $active }) => ($active ? T.border : 'transparent')};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? T.surface : 'transparent')};
  color: ${({ $active }) => ($active ? T.text : T.text2)};
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  /* «Jak w systemie» шире трети 390 px — две строки, а не вылет за рамку. */
  line-height: 15px;
  text-align: center;
  cursor: pointer;

  & > svg {
    flex: none;
  }
`;

const ExitRow = styled.button`
  ${rowBase}
  margin-top: 12px;
  color: ${T.danger};
`;
